import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@h2/ui';
import { Input } from '@h2/ui';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@h2/ui';
import { Badge } from '@h2/ui';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Globe, 
  Hash, 
  Clock,
  MoreHorizontal,
  Filter,
  FileText,
  CheckCircle,
  User,
  Video,
  Settings
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@h2/ui';
import { useTaskTypeStore, TaskType } from '../stores/useTaskTypeStore';
import { TaskTypeFormModal } from '../components/task-type/TaskTypeFormModal';
import { DeleteConfirmModal } from '../components/task-type/DeleteConfirmModal';

// 아이콘 매핑 객체
const iconMap = {
  FileText,
  Search,
  Globe,
  CheckCircle,
  User,
  Video,
  Settings,
  // 기본 아이콘들
  Hash,
  Clock,
  Edit,
  Trash2,
  Plus,
  Filter
};

// 아이콘 렌더링 컴포넌트
const TaskTypeIcon: React.FC<{ iconName: string; className?: string }> = ({ iconName, className = "h-6 w-6" }) => {
  const IconComponent = iconMap[iconName as keyof typeof iconMap];
  return IconComponent ? <IconComponent className={className} /> : <Settings className={className} />;
};

export const TaskTypeManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    filteredTaskTypes,
    isLoading,
    searchQuery,
    loadTaskTypes,
    setSearchQuery,
    updateTaskType,
    deleteTaskType
  } = useTaskTypeStore();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTaskType, setEditingTaskType] = useState<TaskType | null>(null);
  const [deletingTaskType, setDeletingTaskType] = useState<TaskType | null>(null);
  const [filterActive, setFilterActive] = useState<boolean | null>(null);

  useEffect(() => {
    console.log('TaskTypeManagementPage: Loading task types...');
    loadTaskTypes();
  }, [loadTaskTypes]);

  // 디버깅용 로그
  console.log('TaskTypeManagementPage: filteredTaskTypes', filteredTaskTypes);
  console.log('TaskTypeManagementPage: isLoading', isLoading);
  console.log('TaskTypeManagementPage: searchQuery', searchQuery);
  console.log('TaskTypeManagementPage: filterActive', filterActive);
  
  // 실제 렌더링되는 아이템 수 계산
  const finalFilteredItems = filteredTaskTypes.filter(taskType => 
    filterActive === null || taskType.isActive === filterActive
  );
  console.log('TaskTypeManagementPage: finalFilteredItems', finalFilteredItems);

  const handleEdit = (taskType: TaskType) => {
    setEditingTaskType(taskType);
  };

  const handleDelete = (taskType: TaskType) => {
    setDeletingTaskType(taskType);
  };

  const handleToggleActive = async (taskType: TaskType) => {
    await updateTaskType(taskType.id, { isActive: !taskType.isActive });
  };

  const handleFormSave = () => {
    setShowCreateForm(false);
    setEditingTaskType(null);
  };

  const handleFormCancel = () => {
    setShowCreateForm(false);
    setEditingTaskType(null);
  };

  const handleDeleteConfirm = async () => {
    if (deletingTaskType) {
      await deleteTaskType(deletingTaskType.id);
      setDeletingTaskType(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* 헤더 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">작업유형 관리</h1>
          <p className="text-muted-foreground">
            워크플로우에서 사용할 작업 유형을 관리합니다
          </p>
        </div>
        <Button onClick={() => navigate('/work/task-types/create')}>
          <Plus className="h-4 w-4 mr-2" />
          새 작업유형
        </Button>
      </div>

      {/* 검색 및 필터 */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="작업유형 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <Filter className="h-4 w-4 mr-2" />
              필터
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => setFilterActive(null)}>
              전체
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilterActive(true)}>
              활성만
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilterActive(false)}>
              비활성만
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* 작업유형 그리드 */}
      {isLoading ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">작업유형을 불러오는 중...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {finalFilteredItems.map((taskType) => (
            <TaskTypeCard
              key={taskType.id}
              taskType={taskType}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
            />
          ))}
        </div>
      )}

      {/* 빈 상태 */}
      {finalFilteredItems.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">📝</div>
          <h3 className="text-lg font-semibold mb-2">작업유형이 없습니다</h3>
          <p className="text-muted-foreground mb-4">
            첫 번째 작업유형을 생성해 보세요
          </p>
          <Button onClick={() => setShowCreateForm(true)}>
            <Plus className="h-4 w-4 mr-2" />
            작업유형 생성
          </Button>
        </div>
      )}

      {/* 모달들 */}
      {showCreateForm && (
        <TaskTypeFormModal
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      )}

      {editingTaskType && (
        <TaskTypeFormModal
          taskType={editingTaskType}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      )}

      {deletingTaskType && (
        <DeleteConfirmModal
          taskType={deletingTaskType}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeletingTaskType(null)}
        />
      )}
    </div>
  );
};

interface TaskTypeCardProps {
  taskType: TaskType;
  onEdit: (taskType: TaskType) => void;
  onDelete: (taskType: TaskType) => void;
  onToggleActive: (taskType: TaskType) => void;
}

const TaskTypeCard: React.FC<TaskTypeCardProps> = ({
  taskType,
  onEdit,
  onDelete,
  onToggleActive
}) => {
  return (
    <Card className={`hover:shadow-md transition-shadow ${!taskType.isActive ? 'opacity-60' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
              <TaskTypeIcon iconName={taskType.icon} className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg">{taskType.name}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                {taskType.languageDependent ? (
                  <Badge variant="secondary" className="text-xs">
                    <Globe className="h-3 w-3 mr-1" />
                    다국어
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">
                    <Globe className="h-3 w-3 mr-1" />
                    언어무관
                  </Badge>
                )}
                <Badge variant="outline" className="text-xs">
                  <Hash className="h-3 w-3 mr-1" />
                  {taskType.order}
                </Badge>
              </div>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => onEdit(taskType)}>
                <Edit className="h-4 w-4 mr-2" />
                편집
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onToggleActive(taskType)}>
                {taskType.isActive ? '비활성화' : '활성화'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => onDelete(taskType)}
                className="text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                삭제
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent>
        <CardDescription className="line-clamp-2">
          {taskType.memo}
        </CardDescription>
      </CardContent>
    </Card>
  );
};
